import z from 'zod'
import { teacherEmploymentSetting } from '../redux/types'
import { DayjsSchema } from '../schemas/helper'

type AccreditationConfig = {
    states: {
        [stateName: string]: StateAccreditationConfig
    }
    documents: Record<string, AccreditationDocumentConfig>
}

type StateAccreditationConfig = {
    long_name: string
    school_docs: Record<string, boolean>
    ecc_docs: Record<string, boolean>
    supportStaff_docs: Record<string, boolean>
}

const accFrequency = z.enum(['Current', 'Annual', 'Once'] as const)
type AccFrequency = z.infer<typeof accFrequency>

const accRequiredDateField = z.enum(['expiryDate', 'registrationDate'] as const)
type AccRequiredDateField = z.infer<typeof accRequiredDateField>

const overridesByEmploymentType = z.record(
    teacherEmploymentSetting,
    z.record(z.string(), z.string()).optional()
)

export const accreditationDocFormSchema = z.looseObject({
    imageURL: z
        .union([
            z.string().min(2),
            z.file().mime(['image/png', 'image/jpeg', 'application/pdf']),
        ])
        .optional(),
    reference: z.string().optional(),
    registrationDate: DayjsSchema.optional(),
    expiryDate: DayjsSchema.optional(),

    qualification: z.string().optional(),
    classification: z.string().optional(),

    courseType: z.string().optional(),

    renewal_frequency_years: z.number().optional(),
    website_link: z.string().optional(),
})

const baseInput = z.object({
    key: z.string(),
    label: z.string().optional(),
    required: z.boolean().optional(),
})

const dateInput = baseInput.extend({
    input: z.literal('date'),
})

const textInput = baseInput.extend({
    input: z.literal('text'),
    placeholder: z.string().optional(),
    helperText: z.string().optional(),
    pattern: z
        .string()
        .optional()
        .describe('Regex pattern for input validation'),
})

const multiSelectInput = baseInput.extend({
    input: z.literal('select'),
    multiple: z.boolean(),
    options: z.array(
        z.looseObject({
            label: z.string(),
            value: z.string(),
        })
    ),
})

const accreditationFields = z.discriminatedUnion('input', [
    textInput,
    dateInput,
    multiSelectInput,
])

export const accreditationDocumentConfigSchema = z.object({
    // id: z.string(),

    primary: z
        .literal(true)
        .nullable()
        .default(null)
        .describe(
            'Flag to show that the accreditation type is a primary document for the employment type.'
        ),
    type: z.string(),
    short_title: z.string(),
    optional: z.boolean().nullable().default(false),
    long_title: z.string(),
    frequency: z.string().nullable().default(null),
    // frequency: accFrequency,
    requirement_description: z.string(),
    website_link: z.string(),
    details_description: z.string(),
    upload_instructions: z.string().nullable().default(null),
    required_datefield: accRequiredDateField.nullable().default(null),
    renewal_frequency_years: z.number().nullable().default(null),
    // no_reference: z.literal(true).nullable().default(null),
    reference_required: z
        .boolean()
        .default(true)
        .describe('If true | null, then show. If false then hide'),

    fields: z.array(accreditationFields).optional(),

    by_employment_type: overridesByEmploymentType.optional(),
})

const accreditationConfigStateDocSchema = z.object({
    primary: z
        .literal(true)
        .nullable()
        .default(null)
        .describe(
            'Flag to show that the accreditation type is a primary document for the employment type.'
        ),
    optional: z
        .boolean()
        .nullable()
        .default(null)
        .describe(
            'Flag to show whether the document is optional for this employment type'
        ),
    title: z
        .string()
        .default('')
        .describe('Short title of the accreditation doc'),
})
const accreditationDocumentsForSetting = z.record(
    z.string(),
    accreditationConfigStateDocSchema
)

export type AccreditationConfigStateDoc = z.infer<
    typeof accreditationConfigStateDocSchema
>
export type AccreditationDocumentsForSetting = z.infer<
    typeof accreditationDocumentsForSetting
>

export type AccreditationDocumentConfig = z.infer<
    typeof accreditationDocumentConfigSchema
>
